import { useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { can_visit, getPractice, getSecureData, notify, removeSecureItems, role_has, setSecureData, userAccessToken } from "./helpers/index.js";
import Layout from "./components/Layout";
import Dashboard from "./components/Pages/Dashboard";
import HighRiskPatients from "./components/Pages/OverviewCardDetails/HighRiskPatients";
import Appointments from "./components/Pages/Appointments";
import WaitingRoom from "./components/Pages/WaitingRoom";
import PatientDatabase from "./components/Pages/PatientDatabase";
import OrderDatabase from "./components/Pages/OrderDatabase";
import AICharting from "./components/Pages/AICharting";
import CareGapPlus from "./components/Pages/CareGapPlus";
import Review from "./components/Pages/Review";
import PendingSubmission from "./components/Pages/OverviewCardDetails/PendingSubmission";
import LabReportMissed from "./components/Pages/OverviewCardDetails/LabReportMissed";
import TotalRejections from "./components/Pages/OverviewCardDetails/TotalRejections";
import PreventativeDue from "./components/Pages/OverviewCardDetails/PreventativeDue";
import HavenNotVisitedInOneYear from "./components/Pages/OverviewCardDetails/HavenNotVisitedInOneYear";
import PendingAppointments from "./components/Pages/OverviewCardDetails/PendingAppointments";
import ConfirmedAppointments from "./components/Pages/OverviewCardDetails/ConfirmedAppointments";
import ExpiredAppointments from "./components/Pages/OverviewCardDetails/ExpiredAppointments";
import UnreadMessages from "./components/Pages/OverviewCardDetails/UnreadMessages";
import FrontdesksDashboard from "./components/Pages/FrontdesksDashboard";
import Login from "./components/Pages/Login";
import BulkMessages from "./components/Pages/Messages/BulkMessages";
import MessageTemplates from "./components/Pages/Messages/MessageTemplates";
import WebsiteMessages from "./components/Pages/Messages/WebsiteMessages";
import UIComponent from "./components/Pages/UIComponent";
import WebsiteSeo from "./components/Pages/WebsiteSeo";
import WithAuth from "./ProtectedRoute.js";
import WithoutAuth from "./PublicRoute.js";
import NotFound from "./components/NotFound/index.jsx";
import Invitations from "./components/Pages/Invitations.jsx";
import Users from "./components/Pages/Users.jsx";
import SignUp from "./components/Pages/SignUp";
import NewAICharting from "./components/Pages/NewAICharting.jsx";
import SummaryAICharting from "./components/Pages/SummaryAICharting.jsx";
import NoPracticesFoundPage from "./components/NoPracticesFoundPage/index.jsx";
import SignOutSheet from "./components/Pages/SignOutSheet.jsx";
import Shoutbox from "./components/Pages/Shoutbox.jsx";
import AuditReport from "./components/Pages/AuditReport.jsx";
import FormQRScanner from "./components/Pages/FormQRScanner.jsx";
import OnboardPractice from "./components/Pages/Administrator/OnboardPractice.jsx";
import OnboardPracticeUploadFiles from "./components/Pages/Administrator/OnboardPracticeUploadFiles.jsx";
import AverateRevenuePerEncounter from "./components/Pages/AverateRevenuePerEncounter.jsx";
import ReferNewPatient from "./components/Pages/ReferNewPatient.jsx";
import ReferralPrint from "./components/Pages/ReferralPrint.jsx";
import VerifyOtp from "./components/Pages/VerifyOtp.jsx";
import OtpMethod from "./components/Pages/OtpMethod.jsx";
import AdminLayout from './components/Layout/AdminLayout';
import Credits from './components/Pages/Admin/Credits';
import Settings from './components/Pages/Admin/Settings';
import Members from './components/Pages/Admin/Members';
import Support from './components/Pages/Admin/Support';
import SetupOTPPage from "./components/Pages/Admin/OtpSetup.jsx";
import SetupSchedulerPage from "./components/Pages/SchedulerSetup.jsx";
import ForgotPassword from "./components/Pages/ForgotPassword.jsx";
import ResetPassword from "./components/Pages/ResetPassword.jsx";
import ChangePassword from "./components/Pages/ChangePassword.jsx";
import LetterCredits from "./components/Pages/Admin/LetterCredits.jsx";
import BulkLetters from "./components/Pages/Letters/BulkLetters.jsx";
import FormsPDFView from "./components/Pages/Forms/FormsPDFView.jsx";
import AdvancedDirectiveForm from "./components/Pages/Forms/AdvancedDirective.jsx";
import HipaaConsentForm from "./components/Pages/Forms/HipaaConsent.jsx";
import OrderCareForm from "./components/Pages/Forms/OlderCare.jsx";
import PatientIntakeForm from "./components/Pages/Forms/PatientIntake.jsx";
import PatientSurveyForm from "./components/Pages/Forms/PatientSurvey.jsx";
import DotPhysicalForm from "./components/Pages/Forms/DotPhysical.jsx";
import ImmunizationForm from "./components/Pages/Forms/Immunization.jsx";
import AdvancedDirectivePrint from "./components/Pages/Forms/Print/AdvancedDirectivePrint.jsx";
import HipaaConsentPrint from "./components/Pages/Forms/Print/HipaaConsentPrint.jsx";
import OlderCarePrint from "./components/Pages/Forms/Print/OlderCarePrint.jsx";
import PatientIntakePrint from "./components/Pages/Forms/Print/PatientIntakePrint.jsx";
import PatientSurveyPrint from "./components/Pages/Forms/Print/PatientSurveyPrint.jsx";
import ImmunizationPrint from "./components/Pages/Forms/Print/ImmunizationPrint.jsx";
import DotPhysicalPrint from "./components/Pages/Forms/Print/DotPhysicalPrint.jsx";
import CarePlanPrint from "./components/Pages/Forms/Print/CarePlanPrint.jsx";
import CommunityAssessmentPrint from "./components/Pages/Forms/Print/CommunityAssessmentPrint.jsx";
import RejectionTracker from "./components/Pages/RejectionTracker.jsx";
import CommunityAssessment from "./components/Pages/Forms/CommunityAssessment.jsx";
import AppointmentTimeSlot from "./components/Pages/Admin/AppointmentTimeSlot.jsx";
import PatientSurveyPrintStatus from "./components/Pages/Forms/Print/PatientSurveyPrintStatus.jsx";
import Invoices from "./components/Pages/Admin/Invoices.jsx";
import InvoicePrint from "./components/Pages/InvoicePrint.jsx";
import Billing from "./components/Pages/Billing.jsx";
import BillingPaymentTracker from "./components/Pages/BillingPaymentTracker.jsx";
import FeatureConfigs from "./components/Pages/Administrator/FeatureConfigs.jsx";
import PermissionSettings from "./components/Pages/Administrator/PermissionSettings.jsx";
import RoleSettings from "./components/Pages/Administrator/RoleSettings.jsx";
import Prompt from "./components/Pages/Administrator/Prompt.jsx";
import CallNote from "./components/Popup/CallNote.jsx";
import AuditReports from "./components/Pages/Audit/AuditReports.jsx";
import PracticeManagement from "./components/Pages/Administrator/PracticeManagement.jsx";
import CCMDatabase from "./components/Pages/CCMDatabase.jsx";
import DummyModeSetup from "./components/Pages/Admin/DummyModeSetup.jsx";
import CreateDocFlow from "./components/Pages/CreateDocFlow.jsx";
import PromptPlayground from "./components/Pages/PromptPlayground.jsx";
import ViewAllNotifications from "./components/Pages/ViewAllNotifications.jsx";
import NPIDatabase from "./components/Pages/Administrator/NPIDatabase.jsx";
import InsuranceGroups from "./components/Pages/Administrator/InsuranceGroups.jsx";
import PasswordManager from "./components/Pages/PasswordManager.jsx";
import DocflowCategory from "./components/Pages/Administrator/DocflowCategory.jsx";
import GenerateParientReport from "./components/Pages/GenerateParientReport.jsx";
import AuditProgressRunning from "./components/Pages/Audit/AuditProgressRunning.jsx";
import AuditDashboard from "./components/Pages/Audit/AuditDashboard.jsx";
import SupportMessages from "./components/Pages/SupportMessages.jsx";
import SingleSupportMessage from "./components/Section/SingleSupportMessage.jsx";
import UserProfile from "./components/Pages/UserProfile.jsx";
import IncomingReferral from "./components/Pages/IncomingReferral.jsx";
import OutgoingReferral from "./components/Pages/OutgoingReferral.jsx";
import { apiServicesUrl } from "./services/baseUrl.js";
import LiveTranscription from "./components/Pages/LiveTranscription.jsx";
import {
  setAccessToken,
  setRefreshToken,
  setPracticeInfo,
  setSelectedSpeciality,
  setAccountNames,
  setLocationNames,
  setInsurancesList,
  setFeatureConfigs,
  setCaregapAdditionalData,
  setMessageTemplatesData,
  setPendingQmData,
} from "./Session";
import ChartingHelperModal from "./components/Modal/ChartingHelperModal.jsx";
import ClinicalNotes from "./components/Pages/ClinicalNotes.jsx";
import MIPS from "./components/Pages/MIPS.jsx";
import VeradigmConnection from "./components/Pages/VeradigmConnection.jsx";
import UpdatePassword from "./components/Pages/UpdatePassword.jsx";
import ResumeSetup from "./components/Pages/ResumeSetup.jsx";
import FidelisPatient from "./components/Pages/FidelisPatient.jsx";
import OutgoingFidelisMessages from "./components/Pages/Messages/OutgoingFidelisMessages.jsx";
import IncomingFidelisMessages from "./components/Pages/Messages/IncomingFidelisMessages.jsx";
import PracticeDataSync from "./components/Pages/PracticeDataSync.jsx";
import NewOnboardPractice from "./components/Pages/NewOnboardPractice.jsx";
import BAAAgreementSign from "./components/Pages/BAAAgreementSign.jsx";
import NPIManagement from "./components/Pages/Admin/NPIManagement.jsx";
import EligibilityLogs from "./components/Pages/EligibilityLogs.jsx";
import IPADataImport from "./components/Pages/IPADataImport.jsx";
import ExIpaManagement from "./components/Pages/ExIpaManagement.jsx";
import ErrorLogs from "./components/Pages/Administrator/ErrorLogs.jsx";
import IPADashboard from "./components/Pages/IPADashboard.jsx";

function App() {
  const navigate = useNavigate();
  const [changePracticeSpeciality, setChangePracticeSpeciality] = useState(null);
  const [selectedPracticeChange, setSelectedPracticeChange] = useState(null);
  const [fetchChangePracticeData, setFetchChangePracticeData] = useState(false);
  const [smsLimit, setSmsLimit] = useState({});
  const [letterLimit, setLetterLimit] = useState({});
  const [showPrevisitLoader, setShowPrevisitLoader] = useState(false);
  const [showPrevisitLoaderData, setShowPrevisitLoaderData] = useState({});
  const [previsitButtonLoader, setPrevisitButtonLoader] = useState(false);
  const [waitingRoomProviders, setWaitingRoomProviders] = useState([]);
  const [showAICharting, setShowAICharting] = useState(false);
  const [ipaData, setIpaData] = useState({});
  const [loadProfile, setLoadProfile] = useState(false);


  // ======::: Session Info :::
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const updateSessionData = async () => {
    const accessTokenData = await getSecureData("accessToken");
    setAccessToken(accessTokenData);
    const practiceData = await getSecureData("practiceInfo");
    if (practiceData) {
      setPracticeInfo(practiceData);    
    }
    const featureConfigsData = await getSecureData("feature_configs");
    if (featureConfigsData) {
      setFeatureConfigs(featureConfigsData);
    }
    const refreshTokenData = await getSecureData("refreshToken");
    const specialityData = await getSecureData("practiceSpecialityInfo");
    const accountNamesData = await getSecureData("account_names");
    const locationNamesData = await getSecureData("location_names");
    setSelectedSpeciality(specialityData);
    setAccountNames(accountNamesData);
    setLocationNames(locationNamesData);
    setRefreshToken(refreshTokenData);
    const caregapAdditionalData = await getSecureData("caregapAdditionalData");
    setCaregapAdditionalData(caregapAdditionalData);
    const messageTemplatesData = await getSecureData("message_templates");
    setMessageTemplatesData(messageTemplatesData);
    const pendingQmData = await getSecureData("pending_qm_data");
    setPendingQmData(pendingQmData);
  };
  
  useEffect(() => {
    updateSessionData();
    
    const observer = new MutationObserver(([mutation]) => {
      if (
        mutation.type === "attributes" &&
        mutation.attributeName === "style" &&
        mutation.target === document.body
      ) {
        updateSessionData();
      }
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });
    return () => observer.disconnect();
  }, []);

  
  const updateOneTimeSessionData = async () => {
    const insurancesData = await getSecureData("practice_insurances");
    setInsurancesList(insurancesData);     
  };
  
  useEffect(() => {
    updateOneTimeSessionData();
  }, []);

  const prepareSessionData = () => {
    const intervalSession = setInterval(async () => {
      if (getPractice("location_id") > 0) {
        setIsDataLoaded(true);
        clearInterval(intervalSession);        
      }
      const isLoginPage = window.location.pathname.includes("/login");
      if (!isLoginPage) {
        try {
          if (getPractice("location_id") < 1) {
            await updateSessionData();
            await updateOneTimeSessionData();
            setIsDataLoaded(true); // Set the flag
            clearInterval(intervalSession); // Stop retrying
          }
        } catch (error) {}
      }
    }, 500);
  };

  useEffect(() => {
    prepareSessionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataLoaded]);


  // ======::: End Session Info :::
  useEffect(() => {
    if (selectedPracticeChange != null && fetchChangePracticeData) {
      setFetchChangePracticeData(false);
    }
  }, [selectedPracticeChange, fetchChangePracticeData]);

  useEffect(() => {
    const checkPrevisitDownloadData = async () => {
      const _previsitDownloadFilter = await getSecureData("previsitDownloadFilter");
      const _previsitDownloadLastId = await getSecureData("previsitDownloadLastId");

      if (_previsitDownloadFilter || _previsitDownloadLastId) {
        downloadAllPrevisit(_previsitDownloadFilter, _previsitDownloadLastId);
      }
    };
    checkPrevisitDownloadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadAllPrevisit = async (filterData, lastWaitingID = null, providerName = null) => {
    setPrevisitButtonLoader(true);
    await setSecureData("previsitDownloadFilter",JSON.stringify(filterData, lastWaitingID));
    await setSecureData("previsitDownloadLastId", lastWaitingID);

    const {
      start_date,
      end_date,
      account_id,
      location_id,
      speciality_id,
      provider_id,
    } = filterData;

    let specialityId = speciality_id ? `&speciality_id=${speciality_id}` : "";
    let startDateParam = start_date ? `&start_date=${start_date}` : "";
    let endDateParam = end_date ? `&end_date=${end_date}` : "";
    let providerId = provider_id ? `&provider_id=${provider_id}` : "";
    let url = "";
    if (lastWaitingID !== null) {
      url = `${apiServicesUrl}/waiting_rooms/previsit_download?account_id=${account_id}&location_id=${location_id}${specialityId}${startDateParam}${endDateParam}${providerId}&last_read=${lastWaitingID}`;
    } else {
      url = `${apiServicesUrl}/waiting_rooms/previsit_download?account_id=${account_id}&location_id=${location_id}${specialityId}${startDateParam}${endDateParam}${providerId}`;
    }

    try {
      if (!userAccessToken()) return;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
          Authorization: `Bearer ${userAccessToken()}`,
        },
      });

      if (response.status === 202) {
        const errorData = await response.json();
        setShowPrevisitLoader(true);
        setShowPrevisitLoaderData(errorData?.detail);
        await setSecureData("previsitDownloadData", JSON.stringify(errorData?.detail));

        setTimeout(async () => {
          const _previsitDownloadData = await getSecureData("previsitDownloadData");
          if (_previsitDownloadData) {
            downloadAllPrevisit(filterData, errorData?.detail?.last_read, providerName);
          }
        }, 5000);
        return;
      }

      const contentType = response.headers.get("content-type") || "";

      if (!response.ok) {
        if (contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to download the report.");
        } else {
          throw new Error("Server returned an unexpected response.");
        }
      }

      if (!contentType.includes("application/pdf")) {
        throw new Error("Invalid response format. Expected a PDF file.");
      }

      const blob = await response.blob();
      const fileURL = window.URL.createObjectURL(blob);

      let fileName = `Previsit-Report${providerName ? ` (${providerName})` : ''}.pdf`;
      const contentDisposition = response.headers.get("content-disposition");
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          const baseName = decodeURIComponent(match[1].replace(/\.pdf$/, ""));
          fileName = `${baseName}${providerName ? ` (${providerName})` : ''}.pdf`;
        }
      }

      const link = document.createElement("a");
      link.href = fileURL;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(fileURL);
      setShowPrevisitLoaderData({});
      setShowPrevisitLoader(false);
      setPrevisitButtonLoader(false);
      await removeSecureItems([
        "previsitDownloadData",
        "previsitDownloadFilter",
        "previsitDownloadLastId",
      ]);
    } catch (err) {
      console.error("Failed to download previsit report:", err);
      notify("error", err.message, 2);
      setPrevisitButtonLoader(false);
    }
  };


  useEffect(() => {
    const rootElement = document.getElementById("root");

    const observeDrawer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.attributeName === "aria-hidden" &&
          rootElement?.getAttribute("aria-hidden") === "true"
        ) {
          const activeDrawer = document.activeElement;
          if (
            activeDrawer instanceof HTMLElement &&
            rootElement.contains(activeDrawer)
          ) {
            activeDrawer.blur();
          }
        }
      }
    });

    if (rootElement) {
      observeDrawer.observe(rootElement, { attributes: true });
    }
    return () => observeDrawer.disconnect();
  }, []);

  useEffect(() => {
    const checkRedirect = async () => {
      const practiceData = await getSecureData("practiceInfo");

      if(practiceData && practiceData?.require_change_password === true) {
        navigate("/update-password", { replace: true });
        return;
      }
      if (can_visit('ehr_veradigm') && practiceData?.is_provider === true && !role_has("Admin") &&(!practiceData?.ehr_type || !practiceData?.ehr_username)) {
        navigate("/connect-veradigm", { replace: true });
      }
      if((practiceData && typeof practiceData?.baa_agreement !== "string" && !can_visit("administrator")) || (practiceData && practiceData?.baa_agreement?.trim() === "" && !can_visit("administrator"))) {
        navigate("/baa-agreement", { replace: true });
      }
    };

    setTimeout(() => {
      checkRedirect();
    }, 500);
  }, [navigate]);

  // Define the paths where ChartingHelperModal should NOT appear
  const hideChartingHelperModalPaths = [
    "/connect-veradigm",
    "/no-practices-found",
    "/previsit",
    "/easy-referral/print",
    "/invoice/print",
    "/form-code",
    "/summary-report",
    "/live-transcription",
    "/advanced-directive-form/print",
    "/hipaa-consent-form/print",
    "/older-care-form/print",
    "/patient-intake-form/print",
    "/patient-survey-form/print",
    "/immunization-form/print",
    "/dot-physical-form/print",
    "/care-plan-form/print",
    "/community-assessment-form/print",
    "/baa-agreement",
  ];

  // Check if current path matches any of the excluded paths
  const shouldShowChartingHelperModal = can_visit("administrator") && !hideChartingHelperModalPaths.some((path) =>
    path.endsWith("/") ? window.location.pathname.startsWith(path) : window.location.pathname === path
  );

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <WithAuth
              children={
                <Layout
                  selectedPracticeChange={selectedPracticeChange}
                  setSelectedPracticeChange={setSelectedPracticeChange}
                  fetchChangePracticeData={fetchChangePracticeData}
                  setFetchChangePracticeData={setFetchChangePracticeData}
                  smsLimit={smsLimit}
                  setSmsLimit={setSmsLimit}
                  letterLimit={letterLimit}
                  setLetterLimit={setLetterLimit}
                  setChangePracticeSpeciality={setChangePracticeSpeciality}
                  showPrevisitLoader={showPrevisitLoader}
                  setShowPrevisitLoader={setShowPrevisitLoader}
                  showPrevisitLoaderData={showPrevisitLoaderData}
                  setPrevisitButtonLoader={setPrevisitButtonLoader}
                  setWaitingRoomProviders={setWaitingRoomProviders}
                  showAICharting={showAICharting}
                  ipaProfile={ipaData}
                  setIpaProfile={setIpaData}
                  loadProfile={loadProfile}
                  setLoadProfile={setLoadProfile}
                />
              }
            />
          }
        >
          <Route
            index
            element={
              <WithAuth
                children={
                  can_visit("_role_trial") ? (
                    <AuditDashboard
                      selectedPracticeChange={selectedPracticeChange}
                      fetchChangePracticeData={fetchChangePracticeData}
                      setFetchChangePracticeData={setFetchChangePracticeData}
                    />
                  ) : (
                    ipaData?.ex_ipa?.id ?
                      <IPADashboard />
                    :
                    <Dashboard
                      selectedPracticeChange={selectedPracticeChange}
                      fetchChangePracticeData={fetchChangePracticeData}
                      setFetchChangePracticeData={setFetchChangePracticeData}
                      changePracticeSpeciality={changePracticeSpeciality}
                    />
                  )
                }
              />
            }
          />
          <Route
            path="notification-logs"
            element={
              <WithAuth
                children={
                  <ViewAllNotifications
                    selectedPracticeChange={selectedPracticeChange}
                    fetchChangePracticeData={fetchChangePracticeData}
                  />
                }
              />
            }
          />
          {(can_visit("_role_trial") || can_visit("audit")) && (
            // <Route
            //   path="trial"
            //   element={
            //     <WithAuth
            //       children={
            //         <AuditReport />
            //       }
            //     />
            //   }
            // />

            <Route
              path="audit"
              element={
                <AuditReports
                  selectedPracticeChange={selectedPracticeChange}
                  fetchChangePracticeData={fetchChangePracticeData}
                />
              }
            />
          )}

          <Route path="frontdesks-dashboard" element={<WithAuth children={<FrontdesksDashboard />} />} />
          <Route path="pending-submission" element={<WithAuth children={<PendingSubmission />} />} />
          <Route path="total-rejections" element={<WithAuth children={<TotalRejections />} />} />
          {can_visit("dashboard_overview_lab_report_flu_missed") && (
            <Route
              path="lab-report-missed"
              element={
                <WithAuth
                  children={
                    can_visit("_role_trial") ? (
                      <AuditProgressRunning
                        icon="TestTubeDiagonal"
                        name="Lab Report f/u Missed"
                        show_filter={true}
                        show_back_btn={true}
                        back_btn_link="/"
                        back_btn_text="Back to home"
                      />
                    ) : (
                      <LabReportMissed
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                        smsLimit={smsLimit}
                        setSmsLimit={setSmsLimit}
                        setLetterLimit={setLetterLimit}
                        changePracticeSpeciality={changePracticeSpeciality}
                        waitingRoomProviders={waitingRoomProviders}
                      />
                    )
                  }
                />
              }
            />
          )}
          {can_visit("dashboard_overview_high_risk_patients") && (
            <Route
              path="high-risk-patients"
              element={
                <WithAuth>
                  {can_visit("_role_trial") ? (
                    <AuditProgressRunning
                      icon="Accessibility"
                      name="High-Risk Patients"
                      show_filter={true}
                      show_back_btn={true}
                      back_btn_link="/"
                      back_btn_text="Back to home"
                    />
                  ) : (
                    <HighRiskPatients
                      selectedPracticeChange={selectedPracticeChange}
                      fetchChangePracticeData={fetchChangePracticeData}
                      smsLimit={smsLimit}
                      setSmsLimit={setSmsLimit}
                      setLetterLimit={setLetterLimit}
                      changePracticeSpeciality={changePracticeSpeciality}
                      waitingRoomProviders={waitingRoomProviders}
                    />
                  )}
                </WithAuth>
              }
            />
          )}
          {can_visit("dashboard_overview_annual_wellness") && (
            <Route
              path="annual-wellness"
              element={
                <WithAuth
                  children={
                    can_visit("_role_trial") ? (
                      <AuditProgressRunning
                        icon="Syringe"
                        name="Annual Wellness"
                        show_filter={true}
                        show_back_btn={true}
                        back_btn_link="/"
                        back_btn_text="Back to home"
                      />
                    ) : (
                      <PreventativeDue
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                        smsLimit={smsLimit}
                        setSmsLimit={setSmsLimit}
                        setLetterLimit={setLetterLimit}
                        changePracticeSpeciality={changePracticeSpeciality}
                        waitingRoomProviders={waitingRoomProviders}
                      />
                    )
                  }
                />
              }
            />
          )}
          {can_visit("dashboard_overview_havent_visited") && (
            <Route
              path="haven-not-visited-in-one-year"
              element={
                <WithAuth
                  children={
                    can_visit("_role_trial") ? (
                      <AuditProgressRunning
                        icon="BookUser"
                        name="Haven't Visited In This Year"
                        show_filter={true}
                        show_back_btn={true}
                        back_btn_link="/"
                        back_btn_text="Back to home"
                      />
                    ) : (
                      <HavenNotVisitedInOneYear
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                        smsLimit={smsLimit}
                        setSmsLimit={setSmsLimit}
                        setLetterLimit={setLetterLimit}
                        changePracticeSpeciality={changePracticeSpeciality}
                        waitingRoomProviders={waitingRoomProviders}
                      />
                    )
                  }
                />
              }
            />
          )}
          {can_visit("dashboard_chart_payment_received") && (
            <Route
              path="averate-revenue-per-encounter"
              element={
                <WithAuth
                  children={
                    <AverateRevenuePerEncounter
                      selectedPracticeChange={selectedPracticeChange}
                      fetchChangePracticeData={fetchChangePracticeData}
                      smsLimit={smsLimit}
                      setSmsLimit={setSmsLimit}
                      setLetterLimit={setLetterLimit}
                      changePracticeSpeciality={changePracticeSpeciality}
                      waitingRoomProviders={waitingRoomProviders}
                    />
                  }
                />
              }
            />
          )}
          <Route path="pending-appointments" element={<WithAuth children={<PendingAppointments />} />} />
          <Route path="confirmed-appointments" element={<WithAuth children={<ConfirmedAppointments />} />} />
          <Route path="expired-appointments" element={<WithAuth children={<ExpiredAppointments />} />} />
          <Route path="unread-messages" element={<WithAuth children={<UnreadMessages />} />} />
          {can_visit("waiting_room") && (
            <Route
              path="waiting-room"
              element={
                <WithAuth
                  children={
                    can_visit("_role_trial") ? (
                      <AuditProgressRunning
                        icon="Armchair"
                        name="Waiting room"
                        show_filter={false}
                        show_back_btn={false}
                        back_btn_link=""
                        back_btn_text=""
                      />
                    ) : (
                      <WaitingRoom
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                        setSmsLimit={setSmsLimit}
                        smsLimit={smsLimit}
                        setLetterLimit={setLetterLimit}
                        changePracticeSpeciality={changePracticeSpeciality}
                        downloadAllPrevisit={downloadAllPrevisit}
                        previsitButtonLoader={previsitButtonLoader}
                        waitingRoomProviders={waitingRoomProviders}
                        showPrevisitLoaderData={showPrevisitLoaderData}
                      />
                    )
                  }
                />
              }
            />
          )}
          {can_visit("clinical_notes") && (
            <Route
              path="clinical-notes"
              element={
                <WithAuth
                  children={
                    can_visit("_role_trial") ? (
                      <AuditProgressRunning
                        icon="ClipboardList"
                        name="Clinical notes"
                        show_filter={false}
                        show_back_btn={false}
                        back_btn_link=""
                        back_btn_text=""
                      />
                    ) : (
                      <ClinicalNotes
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                        setSmsLimit={setSmsLimit}
                        smsLimit={smsLimit}
                        setLetterLimit={setLetterLimit}
                        changePracticeSpeciality={changePracticeSpeciality}
                        showPrevisitLoaderData={showPrevisitLoaderData}
                      />
                    )
                  }
                />
              }
            />
          )}
          {can_visit("appointment") && (
            <Route
              path="appointments"
              element={
                <WithAuth
                  children={
                    can_visit("_role_trial") ? (
                      <AuditProgressRunning
                        icon="CalendarDays"
                        name="Appointments"
                        show_filter={false}
                        show_back_btn={false}
                        back_btn_link=""
                        back_btn_text=""
                      />
                    ) : (
                      <Appointments
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                        smsLimit={smsLimit}
                        setSmsLimit={setSmsLimit}
                      />
                    )
                  }
                />
              }
            />
          )}
          {can_visit("patient_database") && (
            <Route
              path="patient-database"
              element={
                <WithAuth
                  children={
                    can_visit("_role_trial") ? (
                      <AuditProgressRunning
                        icon="Contact"
                        name="Patient Database"
                        show_filter={true}
                        show_back_btn={false}
                        back_btn_link=""
                        back_btn_text=""
                      />
                    ) : (
                      <PatientDatabase
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                        smsLimit={smsLimit}
                        setSmsLimit={setSmsLimit}
                        setLetterLimit={setLetterLimit}
                        changePracticeSpeciality={changePracticeSpeciality}
                        waitingRoomProviders={waitingRoomProviders}
                      />
                    )
                  }
                />
              }
            />
          )}
          {can_visit("fidelis_care") && (
            <>
              <Route
                path="fidelis-patient"
                element={
                  <WithAuth
                    children={
                      <FidelisPatient
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                        smsLimit={smsLimit}
                        setSmsLimit={setSmsLimit}
                      />
                    }
                  />
                }
              />
              <Route
                path="outgoing-message"
                element={
                  <WithAuth
                    children={
                      <OutgoingFidelisMessages
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                      />
                    }
                  />
                }
              />
              <Route
                path="incoming-message"
                element={
                  <WithAuth
                    children={
                      <IncomingFidelisMessages
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                      />
                    }
                  />
                }
              />
            </>
          )}
          {can_visit("ccm_database") && (
            <Route
              path="ccm-database"
              element={
                <WithAuth
                  children={ 
                    can_visit('_role_trial') ? (
                      <AuditProgressRunning 
                        icon = "BookUser"
                        name = "CCM Notes"
                        show_filter = {false}
                        show_back_btn = {false}
                        back_btn_link = ""
                        back_btn_text = ""
                      />
                    ) : (
                      <CCMDatabase
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                        smsLimit={smsLimit}
                        setSmsLimit={setSmsLimit}
                      />
                    )
                  }
                />
              }
            />
          )}
          {can_visit("imaging_orders") && (
            <Route
              path="imaging-orders"
              element={
                <WithAuth
                  children={
                    can_visit("_role_trial") ? (
                      <AuditProgressRunning
                        icon="Stethoscope"
                        name="Imaging Orders"
                        show_filter={false}
                        show_back_btn={false}
                        back_btn_link=""
                        back_btn_text=""
                      />
                    ) : (
                      <OrderDatabase
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                        smsLimit={smsLimit}
                        setSmsLimit={setSmsLimit}
                      />
                    )
                  }
                />
              }
            />
          )}
          {can_visit("mips") && (
            <Route
              path="mips"
              element={
                <WithAuth
                  children={
                    can_visit("_role_trial") ? (
                      <AuditProgressRunning
                        icon="FileSpreadsheet"
                        name="MIPS"
                        show_filter={true}
                        show_back_btn={false}
                        back_btn_link=""
                        back_btn_text=""
                      />
                    ) : (
                      <MIPS
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                        smsLimit={smsLimit}
                        setSmsLimit={setSmsLimit}
                      />
                    )
                  }
                />
              }
            />
          )}
          {can_visit("ai_charting") && (
            <>
              <Route
                path="ai-charting"
                element={
                  <WithAuth
                    children={
                      can_visit("_role_trial") ? (
                        <AuditProgressRunning
                          icon="ClipboardPlus"
                          name="AI Charting"
                          show_filter={false}
                          show_back_btn={false}
                          back_btn_link=""
                          back_btn_text=""
                        />
                      ) : (
                        <AICharting
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                          setSmsLimit={setSmsLimit}
                        />
                      )
                    }
                  />
                }
              />
              {can_visit("ai_charting_docflow") && (
                <Route
                  path="docflow"
                  element={
                    <WithAuth
                      children={
                        <CreateDocFlow
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  }
                />
              )}

              {can_visit("ai_charting_docflow") && (
                <Route
                  path="prompt-playground"
                  element={
                    <WithAuth
                      children={
                        <PromptPlayground
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  }
                />
              )}
              {can_visit("ai_charting") && can_visit("ai_charting_create") && (
                <Route
                  path="ai-charting/create"
                  element={
                    <WithAuth
                      children={
                        <NewAICharting
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  }
                />
              )}
              <Route
                path="ai-charting/view"
                element={
                  <WithAuth
                    children={
                      <SummaryAICharting
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                      />
                    }
                  />
                }
              />
            </>
          )}
          {can_visit("caregap_plus") && (
            <Route
              path="caregap-plus"
              element={
                <WithAuth
                  children={
                    can_visit("_role_trial") ? (
                      <AuditProgressRunning
                        icon="HeartHandshake"
                        name="Care Gap"
                        show_filter={true}
                        show_back_btn={false}
                        back_btn_link=""
                        back_btn_text=""
                      />
                    ) : (
                      <CareGapPlus
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                        smsLimit={smsLimit}
                        setSmsLimit={setSmsLimit}
                        setLetterLimit={setLetterLimit}
                        changePracticeSpeciality={changePracticeSpeciality}
                        waitingRoomProviders={waitingRoomProviders}
                      />
                    )
                  }
                />
              }
            />
          )}
          {can_visit("easy_referral") && (
            <>
              <Route
                path="incoming-referral"
                element={
                  <WithAuth
                    children={
                      can_visit("_role_trial") ? (
                        <AuditProgressRunning
                          icon="Repeat2"
                          name="Incoming referral"
                          show_filter={false}
                          show_back_btn={false}
                          back_btn_link=""
                          back_btn_text=""
                        />
                      ) : (
                        <IncomingReferral
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                          changePracticeSpeciality={changePracticeSpeciality}
                        />
                      )
                    }
                  />
                }
              />
              <Route
                path="outgoing-referral"
                element={
                  <WithAuth
                    children={
                      can_visit("_role_trial") ? (
                        <AuditProgressRunning
                          icon="Repeat2"
                          name="Outgoing referral"
                          show_filter={false}
                          show_back_btn={false}
                          back_btn_link=""
                          back_btn_text=""
                        />
                      ) : (
                        <OutgoingReferral
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                          changePracticeSpeciality={changePracticeSpeciality}
                        />
                      )
                    }
                  />
                }
              />
              {can_visit("easy_referral_create") && (
                <Route
                  path="new-referral"
                  element={
                    <WithAuth
                      children={
                        <ReferNewPatient
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                          changePracticeSpeciality={changePracticeSpeciality}
                        />
                      }
                    />
                  }
                />
              )}
            </>
          )}
          {can_visit("rejection_tracker") && (
            <Route
              path="rejection-tracker"
              element={
                <WithAuth
                  children={
                    can_visit("_role_trial") ? (
                      <AuditProgressRunning
                        icon="FolderSync"
                        name="Rejection Tracker"
                        show_filter={true}
                        show_back_btn={false}
                        back_btn_link=""
                        back_btn_text=""
                      />
                    ) : (
                      <RejectionTracker
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                        smsLimit={smsLimit}
                      />
                    )
                  }
                />
              }
            />
          )}
          {can_visit("review") && (
            <Route
              path="review"
              element={
                <WithAuth
                  children={
                    can_visit("_role_trial") ? (
                      <AuditProgressRunning
                        icon="Star"
                        name="Get Reviews"
                        show_filter={false}
                        show_back_btn={false}
                        back_btn_link=""
                        back_btn_text=""
                      />
                    ) : (
                      <Review
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                        smsLimit={smsLimit}
                        setSmsLimit={setSmsLimit}
                      />
                    )
                  }
                />
              }
            />
          )}

          {!can_visit("_role_trial") && (
            <>
              {can_visit("shoutbox") && (
                <Route
                  path="shoutbox"
                  element={
                    <WithAuth
                      children={
                        <Shoutbox
                          selectedPracticeChange={selectedPracticeChange}
                        />
                      }
                    />
                  }
                />
              )}
              {can_visit("password_manager") && (
                <Route
                  path="password-manager"
                  element={
                    <WithAuth
                      children={
                        <PasswordManager
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  }
                />
              )}
              {can_visit("settings") && can_visit("settings_scheduler_setup") && (
                <Route
                  path="text-pilot"
                  element={
                    <SetupSchedulerPage
                      selectedPracticeChange={selectedPracticeChange}
                      fetchChangePracticeData={fetchChangePracticeData}
                    />
                  }
                />
              )}
              {can_visit("practice_data_sync") && (
                <Route
                  path="practice-data-sync"
                  element={
                    <WithAuth
                      children={
                        <PracticeDataSync
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  }
                />
              )}
              {can_visit("administrator") && (
                <Route
                  path="/ipa-data-import"
                  element={
                    <WithAuth
                      children={
                        <IPADataImport
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  }
                />
              )}
              {can_visit("new_practice_onboard") && (
                <Route
                  path="new"
                  element={
                    <NewOnboardPractice />
                  }
                />
              )}
              {can_visit("messages_message_transaction") && (
                <Route
                  path="bulk-message"
                  element={
                    <WithAuth
                      children={
                        <BulkMessages
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                          smsLimit={smsLimit}
                          setSmsLimit={setSmsLimit}
                        />
                      }
                    />
                  }
                />
              )}
              {can_visit("messages_message_templates") && (
                <Route
                  path="message-templates"
                  element={
                    <WithAuth
                      children={
                        <MessageTemplates
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  }
                />
              )}
              {can_visit("letter_letter_transaction") && (
                <Route
                  path="bulk-letter"
                  element={
                    <WithAuth
                      children={
                        <BulkLetters
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  }
                />
              )}
              {can_visit("forms") && (
                <Route path="forms">
                  {can_visit("forms_advanced_directive") && (
                    <Route
                      path="advanced-directive-form"
                      element={
                        <AdvancedDirectiveForm
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  )}
                  {can_visit("forms_hipaa_consent") && (
                    <Route
                      path="hipaa-consent-form"
                      element={
                        <HipaaConsentForm
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  )}
                  {can_visit("forms_older_care") && (
                    <Route
                      path="older-care-form"
                      element={
                        <OrderCareForm
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  )}
                  {can_visit("forms_patient_intake") && (
                    <Route
                      path="patient-intake-form"
                      element={
                        <PatientIntakeForm
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  )}
                  {can_visit("forms_patient_survey") && (
                    <Route
                      path="patient-survey-form"
                      element={
                        <PatientSurveyForm
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  )}
                  {can_visit("forms_dot_physical") && (
                    <Route
                      path="dot-physical-form"
                      element={
                        <DotPhysicalForm
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  )}
                  {can_visit("forms_care_plan") && (
                    <Route
                      path="care-plan-form"
                      element={
                        <FormsPDFView
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  )}
                  {can_visit("forms_community_assessment") && (
                    // <Route
                    //   path="community-assessment-form"
                    //   element={
                    //     <CommunityAssessment
                    //       selectedPracticeChange={selectedPracticeChange}
                    //       fetchChangePracticeData={fetchChangePracticeData}
                    //     />
                    //   }
                    // />
                    <Route
                      path="community-assessment-form"
                      element={
                        <FormsPDFView
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  )}
                  {can_visit("forms_immunization_form") && (
                    <Route
                      path="immunization-form"
                      element={
                        <ImmunizationForm
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  )}
                </Route>
              )}
              {can_visit("billing") && (
                <Route
                  path="billing"
                  element={
                    <WithAuth
                      children={
                        <Billing
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  }
                />
              )}
              {can_visit("administrator") && (
                <>
                  <Route
                    path="website-messages"
                    element={<WithAuth children={<WebsiteMessages />} />}
                  />
                  <Route
                    path="website-seo"
                    element={<WithAuth children={<WebsiteSeo />} />}
                  />
                  <Route
                    path="ui-components"
                    element={<WithAuth children={<UIComponent />} />}
                  />
                </>
              )}
              {can_visit("administrator") && (
                <>
                  {can_visit("administrator") &&
                    can_visit("administrator_onboard_practice") && (
                      <Route
                        path="/onboard-practice"
                        element={<WithAuth children={<OnboardPractice />} />}
                      />
                    )}
                    {can_visit("administrator") &&
                    can_visit("administrator_onboard_practice") && (
                      <Route
                        path="/onboard-practice-upload-files"
                        element={<WithAuth children={<OnboardPracticeUploadFiles />} />}
                      />
                    )}
                  {can_visit("administrator") &&
                    can_visit("administrator_feature_config") && (
                      <Route
                        path="/feature-configs"
                        element={
                          <WithAuth
                            children={
                              <FeatureConfigs
                                selectedPracticeChange={selectedPracticeChange}
                                fetchChangePracticeData={fetchChangePracticeData}
                              />
                            }
                          />
                        }
                      />
                    )}
                  {can_visit("administrator") &&
                    can_visit("administrator_permissions") && (
                      <Route
                        path="/practice-permissions"
                        element={
                          <WithAuth
                            children={
                              <PermissionSettings
                                selectedPracticeChange={selectedPracticeChange}
                                fetchChangePracticeData={fetchChangePracticeData}
                              />
                            }
                          />
                        }
                      />
                    )}
                  {can_visit("administrator") &&
                    can_visit("support_messages") && (
                      <Route
                        path="/support-messages/:caseId?"
                        element={
                          <WithAuth
                            children={
                              <SupportMessages
                                selectedPracticeChange={selectedPracticeChange}
                                fetchChangePracticeData={fetchChangePracticeData}
                              />
                            }
                          />
                        }
                      />
                    )}
                  {can_visit("administrator") &&
                    can_visit("administrator_roles") && (
                      <Route
                        path="/role-permissions"
                        element={
                          <WithAuth
                            children={
                              <RoleSettings
                                selectedPracticeChange={selectedPracticeChange}
                                fetchChangePracticeData={fetchChangePracticeData}
                              />
                            }
                          />
                        }
                      />
                    )}
                  {can_visit("administrator") &&
                    can_visit("administrator_practice_management") && (
                      <Route
                        path="/practice-management"
                        element={<WithAuth children={<PracticeManagement />} />}
                      />
                    )}
                  {can_visit("administrator") &&
                    can_visit("administrator_npi_database") && (
                      <Route
                        path="/npi-database"
                        element={
                          <WithAuth
                            children={
                              <NPIDatabase
                                selectedPracticeChange={selectedPracticeChange}
                                fetchChangePracticeData={fetchChangePracticeData}
                              />
                            }
                          />
                        }
                      />
                    )}
                  {can_visit("administrator") && can_visit("administrator_insurance_group") && (
                    <Route
                      path="/insurance-groups"
                      element={
                        <WithAuth
                          children={
                            <InsuranceGroups
                              selectedPracticeChange={selectedPracticeChange}
                              fetchChangePracticeData={fetchChangePracticeData}
                            />
                          }
                        />
                      }
                    />
                  )}
                  {can_visit("administrator") && (
                    <Route
                      path="/speciality-prompt"
                      element={
                        <WithAuth
                          children={
                            <Prompt
                              selectedPracticeChange={selectedPracticeChange}
                              fetchChangePracticeData={fetchChangePracticeData}
                              changePracticeSpeciality={changePracticeSpeciality}
                            />
                          }
                        />
                      }
                    />
                  )}
                  {can_visit("administrator") && can_visit("administrator_docflow_template") && (
                    <Route
                      path="/docflow-template"
                      element={
                        <WithAuth
                          children={
                            <DocflowCategory
                              selectedPracticeChange={selectedPracticeChange}
                              fetchChangePracticeData={fetchChangePracticeData}
                            />
                          }
                        />
                      }
                    />
                  )}
                  {can_visit("administrator") && (
                    <Route
                      path="/eligibility-logs"
                      element={
                        <WithAuth
                          children={
                            <EligibilityLogs
                              selectedPracticeChange={selectedPracticeChange}
                              fetchChangePracticeData={fetchChangePracticeData}
                            />
                          }
                        />
                      }
                    />
                  )}
                  {can_visit("administrator") && (
                    <Route
                      path="/error-logs"
                      element={
                        <WithAuth
                          children={
                            <ErrorLogs
                              selectedPracticeChange={selectedPracticeChange}
                              fetchChangePracticeData={fetchChangePracticeData}
                            />
                          }
                        />
                      }
                    />
                  )}
                </>
              )}
              {can_visit("administrator") && (
                <Route
                  path="ex-ipa-management"
                  element={
                    <WithAuth
                      children={
                        <ExIpaManagement
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  }
                />
              )}

              <Route
                path="profile"
                element={
                  <UserProfile
                    selectedPracticeChange={selectedPracticeChange}
                    fetchChangePracticeData={fetchChangePracticeData}
                  />
                }
              />

              <Route path="admin" element={<AdminLayout />}>
                {can_visit("settings") &&
                  can_visit("settings_basic_and_location_info") && (
                    <Route
                      path="settings"
                      element={
                        <Settings
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  )}
                <Route path="members" element={<Members />} />
                {can_visit("settings") &&
                  (can_visit("settings_message_credits") ||
                    can_visit("settings_letter_credits")) && (
                    <Route
                      path="credits"
                      element={
                        <Credits
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                          smsLimit={smsLimit}
                          letterLimit={letterLimit}
                        />
                      }
                    />
                  )}
                {can_visit("settings") &&
                  can_visit("settings_letter_credits") && (
                    <Route
                      path="letter-credits"
                      element={
                        <LetterCredits
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                          letterLimit={letterLimit}
                        />
                      }
                    />
                  )}
                {can_visit("settings") && can_visit("settings_support") && (
                  <>
                    <Route
                      path="support"
                      element={
                        <Support
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                    <Route
                      path="support/:caseId"
                      element={<WithAuth children={<SingleSupportMessage />} />}
                    />
                  </>
                )}

                {can_visit("settings") && can_visit("settings_users") && (
                  <>
                    {can_visit("settings_users") &&
                      can_visit("settings_users_invitations") && (
                        <Route
                          path="invitations"
                          element={
                            <WithAuth
                              children={
                                <Invitations
                                  selectedPracticeChange={selectedPracticeChange}
                                  fetchChangePracticeData={fetchChangePracticeData}
                                />
                              }
                            />
                          }
                        />
                      )}
                    {can_visit("settings_users") &&
                      can_visit("settings_users_all_users") && (
                        <Route
                          path="users"
                          element={
                            <WithAuth
                              children={
                                <Users
                                  selectedPracticeChange={selectedPracticeChange}
                                  fetchChangePracticeData={fetchChangePracticeData}
                                />
                              }
                            />
                          }
                        />
                      )}
                  </>
                )}
                {can_visit("settings") && can_visit("settings_otp_setup") && (
                  <Route path="otpsetup" element={<SetupOTPPage />} />
                )}
                {can_visit("settings") &&
                  can_visit("settings_scheduler_setup") && (
                    <Route
                      path="schedulersetup"
                      element={
                        <SetupSchedulerPage
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  )}
                {can_visit("settings") &&
                  (can_visit("settings_dummy_sms_setup") ||
                    can_visit("settings_dummy_call_setup")) && (
                    <Route
                      path="message-call-preferences"
                      element={
                        <DummyModeSetup
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                          smsLimit={smsLimit}
                          setSmsLimit={setSmsLimit}
                        />
                      }
                    />
                  )}
                {can_visit("settings") &&
                  can_visit("settings_appointment_time_setup") && (
                    <Route
                      path="appointment-time-slot"
                      element={
                        <AppointmentTimeSlot
                          selectedPracticeChange={selectedPracticeChange}
                          fetchChangePracticeData={fetchChangePracticeData}
                        />
                      }
                    />
                  )}
                {can_visit("administrator") && (
                  <Route
                    path="invoices"
                    element={
                      <Invoices
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                      />
                    }
                  />
                )}
                {can_visit("practice_npi_management") && (
                  <Route
                    path="npi-management"
                    element={
                      <NPIManagement
                        selectedPracticeChange={selectedPracticeChange}
                        fetchChangePracticeData={fetchChangePracticeData}
                      />
                    }
                  />
                )}
              </Route>
            </>
          )}
        </Route>

        {/* Without Dashboard Layout Pages */}
        <Route path="forms">
          <Route path="advanced-directive-form/print" element={<WithAuth children={<AdvancedDirectivePrint />} />} />
          <Route path="hipaa-consent-form/print" element={<WithAuth children={<HipaaConsentPrint />} />} />
          <Route path="older-care-form/print" element={<WithAuth children={<OlderCarePrint />} />} />
          <Route path="patient-intake-form/print" element={<WithAuth children={<PatientIntakePrint />} />} />
          <Route path="patient-survey-form/print" element={<WithAuth children={<PatientSurveyPrint />} />} />
          <Route path="patient-survey-form/print-status" element={<WithAuth children={<PatientSurveyPrintStatus />} />} />
          <Route path="dot-physical-form/print" element={<WithAuth children={<DotPhysicalPrint />} />} />
          <Route path="care-plan-form/print" element={<WithAuth children={<CarePlanPrint />} />} />
          <Route path="community-assessment-form/print" element={<WithAuth children={<CommunityAssessmentPrint />} />} />
          <Route path="immunization-form/print" element={<WithAuth children={<ImmunizationPrint />} />} />
        </Route>

        <Route path="/no-practices-found" element={<WithAuth children={<NoPracticesFoundPage />} />} />
        <Route path="previsit" element={<WithAuth children={<SignOutSheet />} />} />
        <Route path="baa-agreement" element={<WithAuth children={<BAAAgreementSign />} />} />
        <Route path="/easy-referral/print" element={<WithAuth children={<ReferralPrint />} />} />
        <Route path="/invoice/print" element={<WithAuth children={<InvoicePrint />} />} />
        <Route path="/form-code" element={<WithAuth children={<FormQRScanner />} />} />
        <Route path="/summary-report" element={<WithAuth children={<GenerateParientReport />} />} />
        <Route path="/live-transcription" element={<WithAuth children={<LiveTranscription />} />} />
        <Route path="/login" element={<WithoutAuth children={<Login setLoadProfile={setLoadProfile} />} />} />
        <Route path="/verify-otp" element={<WithoutAuth children={<VerifyOtp />} />} />
        <Route path="/forgot-password" element={<ForgotPassword />}></Route>
        <Route path="/reset-password" element={<ResetPassword />}></Route>
        <Route path="/otp-method" element={<WithoutAuth children={<OtpMethod />} />} />
        <Route path="/signup" element={<WithoutAuth children={<SignUp />} />} />
        <Route path="/change-password" element={<WithoutAuth children={<ChangePassword />} />} />
        <Route path="/connect-veradigm" element={<WithAuth children={<VeradigmConnection />} />} />
        <Route path="/update-password" element={<WithAuth children={<UpdatePassword />} />} />
        <Route path="/resume-setup" element={<ResumeSetup />} />

        <Route path="/p/:shortener_slug" element={<BillingPaymentTracker />} />
        <Route path="/call" element={<CallNote />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {shouldShowChartingHelperModal && (
        <ChartingHelperModal 
          selectedPracticeChange={selectedPracticeChange}
          fetchChangePracticeData={fetchChangePracticeData}
          setShowAICharting={setShowAICharting}
        />
      )}
    </>
  );
}

export default App;
